// Privacy.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { Card } from '../components/UI/card';
import { Button } from '../components/UI/button';
import logo from '../assets/FLUXLAB-Logo.png';
import { Phone, Mail } from 'lucide-react';
import { Link } from "react-router-dom";

const Privacy = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white text-gray-800">
      {/* Navigation - simplified, assuming shared or no motion for now */}
     <motion.nav
  className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm"

  initial={{ y: -100 }}
  animate={{ y: 0 }}
  transition={{ duration: 0.6 }}
>
  <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
    {/* 🟢 Logo Section */}
    <div
      className="flex items-center gap-3 cursor-pointer"
      onClick={scrollToTop}
    >
      <img
        src={logo}
        alt="Company Logo"
        className="w-[110px] h-auto" // Increased by 30%
      />
    </div>

    {/* 🔵 Navigation Links */}
    <div className="hidden md:flex items-center gap-8">
      <a
        href="/"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Home
      </a>
      <a
        href="#about"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        About
      </a>
      <a
        href="#services"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Services
      </a>
      <a
        href="#why-us"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Why Us
      </a>
      <a
        href="#contact"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Contact
      </a>
    </div>

    {/* 🔴 Call-to-Action Button */}
    <a href="#contact">
      <Button className="bg-[#2563eb] hover:bg-red-700 text-white flex items-center">
        <Phone className="w-4 h-4 mr-2" />
        Get Quote
      </Button>
    </a>
  </div>
</motion.nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold mb-6 mt-12"
          >
            Privacy Policy
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl italic tracking-wide font-light"
          >
            Your data protection matters
          </motion.p>
        </div>
      </section>

      {/* Privacy Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="prose prose-lg max-w-none">
          <h4 style={{ color: "#0a4d68", marginTop: "30px" }}>A. INTRODUCTION</h4>
        <p>
          FLUXLAB(“FLUXLAB”) recognizes the importance of privacy of its users and also of maintaining confidentiality of the information provided by its users as a responsible data controller and data processor.
        </p>
        <p>
          This Privacy Policy provides for the practices for handling and securing user's Personal Data (defined hereunder) by FLUXLABand its subsidiaries and affiliates. We'll review this Policy from time to time to make sure it's up-to-date and post the latest version here on this page. When we make significant changes, we'll let you know when you next access our services, or by other communications.
        </p>
        <p>
         This Privacy Policy is applicable to any person in India (‘User’) who accesses or uses the information and content made available by FLUXLAB through its website, mobile site, and mobile app (collectively referred to herein as "Platforms"). For the purpose of this Privacy Policy, wherever the context so requires "you" or "your" shall mean User and the term "we", "us", "our" shall mean FLUXLAB. For the purpose of this Privacy Policy, Website means the website(s), mobile site(s) and mobile app(s).
        </p>
        <p>
         By using or accessing the Platforms, the User hereby agrees with the terms of this Privacy Policy and the contents herein. If you disagree with this Privacy Policy please do not use or access our Platforms. This Privacy Policy does not apply to any website(s), mobile sites and mobile apps of third parties, even if their websites/products are linked to our Website. It is recommended that you review the privacy statements and policies of any such third parties with whom you interact. This Privacy Policy is an integral part of your User Agreement with FLUXLAB.
        </p>
        {/* <p>
          This Privacy Policy is an integral part of your User Agreement with FLUXLAB.
        </p> */}

        {/* ---- B. TYPE OF DATA WE COLLECT ---- */}
        <h4 style={{ color: "#0a4d68", marginTop: "30px" }}>
          B. TYPE OF DATA WE COLLECT AND HOW?
        </h4>

        <h4 style={{ marginTop: "25px" }}>B.1. DATA WE COLLECT:</h4>
        <p>The Data is collected to be able to provide the Services, fulfill our legal obligations, and our obligations towards third parties as per our User Agreement.</p>
        <p>We collect the following Data from you:</p>

        <ul style={{ listStyle: "disc", marginLeft: "30px" }}>
          <li>"Personal Data" of you, which means any data by which you are or can be identified and shall include the information, including but not limited to, about your personal identity such as name, gender, marital status, religion, age, profile picture, your contact details such as your email address, postal addresses, telephone (mobile or otherwise) and/or fax numbers.</li>
          <li>Other Data: We may also collect other information and documents including but not limited to:
<br/>-	User interaction history on our Platforms (e.g., pages viewed, content preferences).
<br/> -	Your usernames, passwords, email addresses and other security-related information used by you in relation to our Services.
<br/> -	Data either created by you or by a third party and which you wish to store on our servers such as image files, documents etc.
<br/> -	User Generated or Provided Content such as comments, photos, videos, recommendations, preferences, reviews, and prompts you enter into AI-powered language models we may make available to you on our Platforms.
<br/> -	Your engagement with us on social media platforms (such as Instagram, Twitter or Facebook) including publicly available information about your social media account.
<br/> -	Data available in public domain or received from any third party including personal or non-personal information from your linked social media channels.
</li>
          <li>Usage Information such as: How you interact with our Platforms and communications, including time you spend on our Platforms, searches performed, and the pages, features or functionality you have accessed, and links you have clicked to be redirected to or from our Platforms.</li>
          <li>Profiling Information: We combine information that we collect, generate, or receive to understand you and your interests better, so that we can personalize our Platforms, services and advertising for you.</li>
        </ul>

        <p>
          FLUXLAB will never share any of the information collected without your prior consent unless such action is required by any law enforcement authority for investigation, by court order or in reference to any legal process.
This Policy doesn't cover the collection or use of your data by the other companies whose products, services, or linked websites we feature on our Platforms ("Third Parties").
        </p>

        <h4 style={{ marginTop: "25px" }}>B.2. HOW WE COLLECT DATA?</h4>
         <p>
         We collect personal data in following ways:
        </p>
        <ul style={{ listStyle: "disc", marginLeft: "30px" }}>
          <li><strong>Data Provided by You:</strong> Data you give to us while registration on the Website/Creating an Account/Signing Up, using the contact us Page for customer support or otherwise communicated to us. If you chose not to provide us with a certain mandatory set of data, we may not be able to provide you the desired services. For Example, If you chose to not provide us your name and contact details, we may not be able to provide you with certain personalized content or services.</li>
          <li><strong>Data Collected Automatically:</strong> We generate or collect some information from your computer or device automatically as you use our Platforms or otherwise interact with us. This includes information like your IP address, cookie identifiers, location, the device and browser you're using, and how you use and interact with our website and other Platforms.</li>
          <li><strong>Data received from third parties:</strong> We are sometimes given information about you from third parties - for example, if you come to our website/Platforms via a promotional partner, or when you log into your FLUXLABaccount using our social network login feature. We may also receive information from fraud prevention partners we cooperate with, to prevent misuse of personal data and protect our systems.</li>
        </ul>

        {/* ---- C. HOW DO WE USE PERSONAL DATA ---- */}
        <h4 style={{ color: "#0a4d68", marginTop: "30px" }}>C. HOW DO WE USE PERSONAL DATA?</h4>
        <p>We only use your personal data where:</p>
        <ul style={{ listStyle: "disc", marginLeft: "30px" }}>
          <li>Necessary for us to perform a contract with you for access to our Platforms and Services;</li>
          <li>Necessary to pursue our or others' legitimate interests;</li>
          <li>Necessary to exercise legal rights or comply with legal obligations; or</li>
          <li>You've given us your consent.</li>
        </ul>

<p>



<p><strong>Performing a Contract:</strong> We use personal data to deliver our Services or facilitate a contract you've entered - or are entering - into with us. In particular, we use personal data to:</p>

• <strong>Make our Platforms work:</strong>We generate results in response to your search queries, for example providing you with a list of the content or informational resources that best meet your requirements.<br/>
• <strong>Send you important communications:</strong>We will send you communications about your account or content subscriptions, your FLUXLABaccount, or other essential information.<br/>
• <strong>Provide customer services.</strong><br/>
• <strong>Maintain your FLUXLAB account.</strong><br/>
• <strong>Run competitions.</strong>


<p><strong>Legitimate Interests:</strong> We use personal data as necessary to pursue our legitimate interests, or those of third parties. In particular, we use personal data to:</p>

• <strong>Make Platforms work:</strong> We identify and correct faults with areas of our Platforms.<br/>
• <strong>Perform Analytics and Improve our Platforms:</strong> We evaluate how, when and why people use FLUXLAB Platforms, and use this information to analyse and improve the Platforms and content we offer.<br/>
• <strong>Personalise your FLUXLAB experience:</strong> We show you information or content that we believe is most relevant to you. For example, if you've recently viewed content about a specific topic, we may display a message asking whether you'd like to search for related topics, or include information about similar content in your next email newsletter.<br/>
• <strong>Ensure Security and Prevent Fraud:</strong> We implement security measures on our Platforms and conduct fraud-prevention activities. We use information about your device and the network you are connected to (such as IP address) to identify suspicious activity.<br/>
• <strong>Perform Customer Satisfaction Surveys and Market Research.</strong><br/>
• <strong>Send Alerts and Marketing Communications.</strong><br/>
• <strong>Keep Records and Run our Business:</strong> We use information for internal administrative and reporting purposes, and to calculate any fees due to FLUXLAB by partners or advertisers.


<p><strong>Consent:</strong> You hereby provide us your consent for processing your personal data for the following purposes:</p>

• <strong>Make our Platforms work.</strong><br/>
• <strong>Send you important communications.</strong><br/>
• <strong>Provide customer services.</strong><br/>
• <strong>Maintain your FLUXLAB account.</strong><br/>
• <strong>Run competitions.</strong><br/>
• <strong>Perform Analytics and Improve our Platforms.</strong><br/>
• <strong>Personalize your FLUXLAB experience.</strong><br/>
• <strong>Ensure Security and Prevent Fraud.</strong><br/>
• <strong>Send Alerts and Marketing Communications.</strong><br/>
• <strong>Keep Records and Run our Business.</strong>
</p>

<p>You have the <strong>Right to Withdraw</strong> your consent provided herein or otherwise for processing of your personal data. If you withdraw your consent, you will not benefit from promotions and offers from us.</p>

        {/* ---- D. USE OF PERSONAL DATA FOR ADVERTISEMENT ---- */}
       <h4 style={{ color: "#0a4d68", marginTop: "30px" }}>D. USE OF PERSONAL DATA FOR ADVERTISEMENT</h4>
<p>
  You’ll see adverts when you use our Platforms and might also see adverts about 
  FLUXLAB and selected FLUXLAB partners when you're 
  on other platforms. The personal data used to personalize adverts may include 
  information you’ve provided by creating an account or subscribing to a service via 
  the FLUXLAB Website, or through cookies and similar technologies.
</p>
<p>
  FLUXLAB partners with affiliate networks and marketing service 
  providers such as Facebook ("Third-Party Ad Solutions") to market and advertise 
  our services on third-party websites and applications and to measure the 
  effectiveness of advertising campaigns.
</p>

<h4 style={{ color: "#0a4d68", marginTop: "30px" }}>E. COOKIE POLICY</h4>
<p>
  We use cookies and similar technologies to help deliver and optimise our website, 
  improve your experience, and for advertising purposes depending on your privacy 
  settings. Please see our <Link
                to="/Cookie"
                style={{ color: "#0a4d68", textDecoration: "underline" }}
              >
                Cookie Policy
              </Link>  for more information.
</p>

<h4 style={{ color: "#0a4d68", marginTop: "30px" }}>F. DURATION OF STORAGE OF PERSONAL DATA</h4>
<p>
  We keep personal data only for as long as we need to. This depends on the purposes 
  for which personal data was collected, and whether we have a continuing need or 
  legal obligation to retain personal data for a certain period. If we no longer need 
  to process your personal data, we will delete it or ensure that it no longer 
  identifies you.
</p>

<h4 style={{ color: "#0a4d68", marginTop: "30px" }}>G. SECURITY OF YOUR PERSONAL DATA</h4>
<p>
  Keeping personal data secure is our highest priority. We provide thorough data 
  protection and privacy training, as well as security training to all ZIPLOGYX 
  PRIVATE LIMITED employees. We use a range of technical, organizational, and 
  administrative security measures and best-practice techniques depending on the 
  type of data being processed. Once your information is in our possession, we 
  adhere to strict security guidelines, protecting it against unauthorized access.
</p>

<h4 style={{ color: "#0a4d68", marginTop: "30px" }}>H. YOUR RIGHTS</h4>
<p>
  You have certain data protection rights, including the right to ask us for a copy 
  of your personal data; to correct, complete, update, or delete your personal data; 
  and to nominate another person who shall, in the event of your death or incapacity, 
  exercise these rights in accordance with the Law. You also have the right to 
  redress your grievance with respect to the processing of your personal data and 
  for exercising your rights under the Law.
</p>

<h4 style={{ color: "#0a4d68", marginTop: "30px" }}>I. CHANGES TO THE PRIVACY POLICY</h4>
<p>
  We reserve the right to revise this Privacy Policy from time to time to meet 
  various legal, business, and customer requirements. We will duly notify users as 
  may be necessary.
</p>


        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div className="text-left">
              <div className="flex items-center gap-3 mb-4 cursor-pointer" onClick={scrollToTop}>
                <img
                  src={logo}
                  alt="FLUXLAB Logo"
                  className="w-[110px] h-auto"
                />
              </div>
              <p className="text-sm leading-relaxed">
                Leading manufacturer of automotive plastic components with a
                decade of excellence.
              </p>
            </div>

            {/* Quick Links */}
            <div className="text-left">
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="/#about"
                    className="hover:text-white transition-colors inline-block"
                  >
                    About Us
                  </a>
                </li>
                <li>
                  <a
                    href="/#services"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Services
                  </a>
                </li>
                <li>
                  <a
                    href="/#why-us"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Why Choose Us
                  </a>
                </li>
                <li>
                  <a
                    href="/#contact"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal Links */}
            <div className="text-left">
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="/terms"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a
                    href="/privacy"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Privacy Policy
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="text-left">
              <h4 className="text-white font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-2">
                  <Phone className="w-4 h-4 mt-1 flex-shrink-0 text-blue-500" />
                  <span>+91 9099159922</span>
                </li>
                <li className="flex items-start gap-2">
                  <Mail className="w-4 h-4 mt-1 flex-shrink-0 text-blue-500" />
                  <span>sales@fluxlab.in</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            <p>
              &copy; {new Date().getFullYear()} FLUXLAB. All rights reserved. | Precision Manufacturing Excellence
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;